import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { GetCookie } from "../provider/common";
import { decodeToken } from "../provider/auth";

// 검색 진행상태 폴링 간격
const POLL_INTERVAL_MS = 1500;
// 이 시간을 넘겨도 서버가 Success 를 주지 않으면 지금까지의 결과로 확정한다.
// PandaSearch 는 벤더 하나가 완료 마킹을 못 남기면 캐시 10분 동안 In Process 에 머문다
// (control-plane#90). 그 경우 로딩이 끝나지 않으므로 클라이언트에서 마감한다.
// 운영 실측 최장 완료가 5.2초여서 정상 검색을 자르지 않는 값으로 잡았다.
const SEARCH_DEADLINE_MS = 8000;

export default function useSearchList(router, keyword) {
  // ====== 검색 결과 상태 ======
  const [datas, setDatas] = useState([]);
  const [filterListManu, setFilterListManu] = useState([]);
  const [filterListSrc, setFilterListSrc] = useState([]);
  const [loadingShow, setLoadingShow] = useState(true);
  const [searchShow, setSearchShow] = useState(false);
  const [noneSearchShow, setNoneSearchShow] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [isLogin, setIsLogin] = useState("0");
  const [member, setMember] = useState(1);

  // ====== 필터 상태 ======
  const [manufacturer, setmMnufacturer] = useState([]);
  const [distributor, setDistributor] = useState([]);
  const [selected, setSelected] = useState([]);

  const SEARCH_API_URL = process.env.SEARCH_API_URL;

  // 폴링 타이머. 재검색·언마운트 때 반드시 정리한다.
  const pollTimerRef = useRef(null);
  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  // 회원등급 조회
  const getMbId = async () => {
    //로그인 했을때만 mb_id를 던지도록 보완
    const isLogin = await GetCookie("isLogin");
    if (isLogin) {
      const user = await decodeToken();
      setMember(user.payload.mb_grade);
    }
  };

  // ✅ 로그인 상태 확인
  const isLoginCheck = useCallback(async () => {
    try {
      const login = await GetCookie("isLogin");
      setIsLogin(login);
    } catch (err) {
      console.error("로그인 상태 확인 중 오류:", err);
    }
  }, []);

  // ✅ 필터(제조사·유통사) 선택 상태 초기화
  const showSelect = useCallback(() => {
    if (!router.query) return;

    const { source, manufacturer } = router.query;

    let selectedSource = source ? source.split(",").filter((e) => e) : [];
    let selectedManufacturer = manufacturer
      ? manufacturer.split(",").filter((e) => e)
      : [];

    if (manufacturer !== "" && source !== "") {
      setDistributor(selectedSource);
      setmMnufacturer(selectedManufacturer);
      setSelected([...selectedManufacturer, ...selectedSource]);
    } else if (manufacturer === "") {
      setDistributor(selectedSource);
      setSelected(selectedSource);
    } else if (source === "") {
      setmMnufacturer(selectedManufacturer);
      setSelected(selectedManufacturer);
    } else {
      setSelected([]);
    }
  }, [router.query]);

  // ✅ 검색 시작 (API 호출)
  const start_search = useCallback(async () => {
    try {
      if (!keyword) return;
      setLoadingShow(true);
      setSearchError(false);

      // 키워드 공백 제거 및 인코딩
      const cleanKeyword = keyword.trimEnd();
      const encodeKeyword = btoa(
        cleanKeyword.startsWith("%")
          ? cleanKeyword
          : encodeURIComponent(cleanKeyword)
      );

      // 필터 적용 여부 확인
      let URL;
      if ("type" in router.query) {
        const { source, manufacturer, pn, sort } = router.query;
        URL = `${SEARCH_API_URL}/search/${encodeKeyword}?type=filter&source=${source}&manufacturer=${manufacturer}&pn=${pn}&sort=${sort}`;
      } else {
        URL = `${SEARCH_API_URL}/search/${encodeKeyword}`;
      }

      // 재검색이면 이전 폴링을 먼저 끊는다. router.query 가 매번 새 객체라
      // start_search 가 재실행되며 타이머가 중첩될 수 있다.
      stopPolling();

      const res = await axios.get(URL);

      // 내부 렌더 처리
      // isFinal = 이 응답이 검색의 마지막 상태인가 (Success 또는 데드라인 도달).
      // 검색이 아직 진행 중(In Process)인 부분결과로는 "결과 없음"을 확정하지 않는다.
      const render = (result, isFinal) => {
        if (!result.data.data?.parts) return;

        const parts = result.data.data.parts;
        const filters = result.data.data.filter;

        setFilterListManu(filters.manufacturers || []);
        const formattedSources = (filters.sources || []).map(
          (name) => name.charAt(0).toUpperCase() + name.slice(1)
        );
        setFilterListSrc(formattedSources);

        if (parts.length > 0) {
          parts.forEach((part) => {
            part.price = (part.price || [])
              .filter(
                (pr) =>
                  pr.source !== "future" &&
                  pr.source !== "corestaff" &&
                  pr.source !== "dfrobot"
              )
              .map((pr) => ({ ...pr, it_id: part.it_id }));
          });
          setDatas(parts);
          setSearchShow(true);
          setNoneSearchShow(false);
          setLoadingShow(false);
        } else if (isFinal) {
          // 검색이 끝났는데 0건 — 이때만 "검색 결과가 없습니다"
          setNoneSearchShow(true);
          setSearchShow(false);
          setLoadingShow(false);
        }
        // 진행 중 0건이면 아무 것도 확정하지 않고 로딩을 유지한다.
        // 하이픈·긴 부품번호는 결과를 가진 벤더(element14·digikey)가 가장 늦게 도착해
        // 0.4~2.0초 동안 parts=[] 인 구간이 생긴다 (control-plane#89).
      };

      // 첫 번째 호출
      if (res.data.msg === "Success") {
        render(res, true);
        return;
      }
      // “In Process”일 때 폴링 처리
      if (res.data.msg === "In Process") {
        // 첫 응답에 이미 부분결과가 있으면 폴링을 기다리지 않고 바로 그린다.
        render(res, false);

        const startedAt = Date.now();
        pollTimerRef.current = setInterval(async () => {
          try {
            const resPolling = await axios.get(URL);
            const isSuccess = resPolling.data.msg === "Success";
            const isDeadline = Date.now() - startedAt >= SEARCH_DEADLINE_MS;
            if (isSuccess || isDeadline) {
              stopPolling();
            }
            render(resPolling, isSuccess || isDeadline);
          } catch (e) {
            console.error("검색 폴링 에러:", e);
            stopPolling();
            setLoadingShow(false);
            setSearchError(true);
            setSearchShow(false);
          }
        }, POLL_INTERVAL_MS);
      }
    } catch (e) {
      console.error("검색 에러:", e);
      stopPolling();
      setLoadingShow(false);
      setSearchError(true);
      setSearchShow(false);
    }
  }, [keyword, router.query, stopPolling]);
  //필터적용 버튼 실행 함수
  const handleFilterSearch = (e) => {
    // 검색된 현재값
    const current = `/search/lists?keyword=${
      keyword.startsWith("%") ? keyword : encodeURIComponent(keyword)
    }`;

    if (e === "all") {
      window.location.href = `/search/lists?keyword=${
        keyword.startsWith("%") ? keyword : encodeURIComponent(keyword)
      }`;
      return;
    }

    // 키워드 검색
    if (e == "keyword") {
      window.location.href = `${current}&type=filtered&manufacturer=${manufacturer}&source=${distributor}&pn=${keyword}&sort=day`;
      return;
    }
    // 라디오 버튼을 눌렀을때 반응하는 실행
    if (e == "day" || e == "qty") {
      window.location.href = `${current}&type=filtered&manufacturer=${manufacturer}&source=${distributor}&pn=&sort=${e}`;
    } else {
      // 필터적용 버튼 눌렀을때 실행
      if (router.query.sort) {
        window.location.href = `${current}&type=filtered&manufacturer=${manufacturer}&source=${distributor}&pn=&sort=${router.query.sort}`;
        // 기본값 필터링은 납기순
      } else {
        window.location.href = `${current}&type=filtered&manufacturer=${manufacturer}&source=${distributor}&pn=&sort=day`;
      }
    }
  };

  const handleFilterSearchFigma = (
    e,
    selectedManufacturer = [],
    selectedDistributor = []
  ) => {
    // 선택된 리스트를 문자열로 변환
    const manufacturer = encodeURIComponent(selectedManufacturer.join(","));
    const distributor = encodeURIComponent(selectedDistributor.join(","));

    // 검색된 현재값
    const current = `/search/lists?keyword=${
      keyword.startsWith("%") ? keyword : encodeURIComponent(keyword)
    }`;

    router.push({
      pathname: "/search/lists",
      query: {
        keyword: keyword.startsWith("%")
          ? keyword
          : encodeURIComponent(keyword),
        type: "filtered",
        manufacturer: manufacturer,
        source: distributor,
        pn: "",
        sort: "day",
      },
    });
  };

  // 정렬 버튼 클릭 시 URL 업데이트
  const handleSortChangeFigma = (sortType) => {
    const { query } = router;

    const newQuery = {
      keyword: query.keyword || "",
      type: "filtered",
      manufacturer: query.sormanufactureruce || "",
      source: query.source || "",
      pn: sortType === "keyword" ? keyword : "",
      sort: sortType,
    };
    router.push({
      pathname: "/search/lists",
      query: newQuery,
    });
  };

  // 제조사 유통사 필터 선택
  const handleFilterSelection = useCallback((e) => {
    let checked = e.target.checked;
    let value = e.target.value.split(" ");
    // 체크되어있으면 추가
    if (checked === true) {
      if (value[0] === "manufacturer") {
        setmMnufacturer((current) => current.concat(value.slice(1).join(" ")));
      } else {
        setDistributor((current) => current.concat(value[1]));
      }
      setSelected((current) => current.concat(value.slice(1).join(" ")));
    } else {
      // 체크 해제 것들은 제거
      if (value[0] === "manufacturer") {
        setmMnufacturer((current) =>
          current.filter((e) => e !== value.slice(1).join(" "))
        );
      } else {
        setDistributor((current) => current.filter((e) => e !== value[1]));
      }
      setSelected((current) =>
        current.filter((e) => e !== value.slice(1).join(" "))
      );
    }
  });

  // 선택초기화 버튼 실행 함수
  const handleResetSearch = () => {
    const current = `/search/lists?keyword=${
      keyword.startsWith("%") ? keyword : encodeURIComponent(keyword)
    }`;
    window.location.href = `${current}`;
  };

  const CombineBotAsked = async (isLoginCheck, data, pics) => {
    if (data.source === "pandaparts") {
      if (isLoginCheck === "0") {
        const href = `/auth/login?before=/parts/inventory/${data.it_id}`;
        router.push(href);
        return;
      }

      if (isLoginCheck === "1") {
        router.push(`/parts/inventory/${data.it_id}`);
        return;
      }
    }

    // 로그인이 되어있으면 견적바구니로 바로 요청
    if (isLoginCheck === "1") {
      const body = [];

      body.push({
        it_id: data.it_id,
        it_brand: data.manufacturer,
        it_name: data.pn,
        it_maker: data.source,
        qty: pics.stock,
      });

      try {
        const URL = process.env.PANDA_API_URL + "/api/mall/insertQuoteLists";
        const result = await axios.post(URL, body, {
          headers: {
            "content-type": "application/json",
            Authorization: `Bearer ${await GetCookie("token")}`,
          },
        });
        if (result.data.status === 200) {
          setQuoteCnt(result.data.quoteAllCnt);
          if (
            confirm(
              "견적바구니에 저장되었습니다. 견적바구니로 이동하시겠습니까?"
            ) === true
          ) {
            router.push(`/marketplace/cart`);
            return;
          }
        } else {
          alert("견적바구니에 저장이 실패하였습니다.");
        }
      } catch (e) {
        console.log(e);
        alert("견적바구니에 저장이 실패하였습니다.");
      }
    } else {
      const basePath = `/search/lists?keyword=${keyword}`;
      const href = `/auth/login?before=${basePath}`;
      router.push(href);
    }
    // 미 로그인 되어있으면 로그인 화면으로 간다.
  };

  // ✅ 초기 실행
  useEffect(() => {
    isLoginCheck();
  }, [isLoginCheck]);

  useEffect(() => {
    start_search();
    // 페이지 이탈·재검색 시 폴링을 남기지 않는다.
    return () => stopPolling();
  }, [start_search, stopPolling]);

  useEffect(() => {
    showSelect();
  }, [showSelect]);
  useEffect(() => {
    getMbId();
  }, [getMbId]);

  // ✅ 반환
  return {
    datas,
    filterListManu,
    filterListSrc,
    loadingShow,
    searchShow,
    noneSearchShow,
    searchError,
    isLogin,
    selected,
    handleFilterSearch,
    handleFilterSelection,
    handleResetSearch,
    CombineBotAsked,
    // 신규 컴퍼넌트 코드 함수
    handleFilterSearchFigma,
    handleSortChangeFigma,
    member,
  };
}
